<template>
  <div class="search-integration">
    <v-autocomplete
      :items="items"
      v-model="item"
      ref="search"
      :get-label="getLabel"
      :component-item='template'
      @update-items="updateItems"
      :auto-select-one-item="false"
      @item-selected="selectedItem"
      placeholder="Search user database..."
      :min-len="3"
    >
    </v-autocomplete>
    <div class="search-status-container">
      <transition name="icon-fade" mode="out-in">
        <div v-if="loading" key="loading" class="icon-wrapper">
          <i class="fa fa-circle-o-notch fa-spin text-primary"></i>
        </div>
        <div v-else key="search" class="icon-wrapper">
          <i class="fa fa-search"></i>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
  import ItemTemplate from './AutocompleteItem.vue'
  import Vue from 'vue'
  import {mapGetters, mapActions} from 'vuex'

  export default {
    name: 'GlobalSearch',
    data () {
      return {
        item: null,
        template: ItemTemplate,
        loading: false,
        debounceTimeout: null
      }
    },
    computed: mapGetters({
      items: 'autocompleteResults'
    }),
    watch: {
      items() {
        this.loading = false
      }
    },
    methods: {
      ...mapActions(['resetItems']),
      getLabel (item) {
        return item ? item.name : ''
      },
      updateItems (text) {
        if (this.debounceTimeout) clearTimeout(this.debounceTimeout)

        if (text && text.length >= 3) {
          this.loading = true
          this.debounceTimeout = setTimeout(() => {
            this.emit('autocomplete', text)
          }, 300)
        } else {
          this.loading = false
          this.resetItems()
        }
      },
      selectedItem (item) {
        if (item) {
          // Pre-populate user servers to avoid redundant network request
          if (item.servers && item.name) {
            this.$store.commit('setUserServers', {
              data: {
                servers: item.servers,
                username: item.name
              }
            });
          }

          // Navigate to the new user.
          // We include the preferred_server (highest playtime) in the query
          const query = {};
          if (item.preferred_server) {
            query.server = item.preferred_server;
          }

          this.$router.push({
            name: 'User',
            params: { username: item.name },
            query: query
          }).catch(() => {});
        }

        // Fix: Reset input and results on next tick to override component behavior
        this.$nextTick(() => {
          this.item = null
          this.resetItems()
          if (this.$refs.search && this.$refs.search.blur) {
            this.$refs.search.blur()
          }
        })
      }
    },
    mounted () {
      // Listen for external focus events if needed
      this.$on('focus', () => {
        if(this.$refs.search && this.$refs.search.focus) {
          this.$refs.search.focus()
          this.resetItems()
        }
      })
    }
  }
</script>

<style scoped>
.search-integration {
  width: 100%;
  position: relative;
}

.search-status-container {
  position: absolute;
  right: 12px;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 2;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #8f9ba6;
  font-size: 14px;
}

/* Polished Transition */
.icon-fade-enter-active, .icon-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.icon-fade-enter {
  opacity: 0;
  transform: scale(0.7) rotate(-15deg);
}
.icon-fade-leave-to {
  opacity: 0;
  transform: scale(1.2) rotate(15deg);
}

/* --- Deep Selectors for Integrated Dark Theme --- */

/* 1. Input Field */
/deep/ .v-autocomplete-input-group .v-autocomplete-input {
  width: 100%;
  /* Standard neutral dark background (#2f353a) - Matches Sidebar/Dark Theme standard */
  background-color: #2f353a;
  border: 1px solid #3e515b; /* Standard gray border */
  color: #fff;
  border-radius: 4px;
  padding: 10px 40px 10px 15px; /* Extra padding right for the icon */
  font-size: 0.95rem;
  transition: all 0.2s ease;
  box-shadow: none;
}

/* 2. Focus State */
/deep/ .v-autocomplete-input-group .v-autocomplete-input:focus {
  background-color: #2f353a;
  border-color: #20a8d8; /* Standard Primary Blue */
  box-shadow: 0 0 0 1px rgba(32, 168, 216, 0.3);
  outline: none;
  color: #fff;
}

/* 3. Placeholder Text */
/deep/ .v-autocomplete-input-group .v-autocomplete-input::placeholder {
  color: #8f9ba6;
  opacity: 0.8;
}

/* 4. Dropdown Results */
/deep/ .v-autocomplete-list {
  background-color: #2f353a; /* Match input background */
  border: 1px solid #3e515b;
  width: 100%;
  margin-top: 5px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.5);
  z-index: 1050;
  max-height: 500px;
  overflow-y: auto;
  border-radius: 4px;
}

/deep/ .v-autocomplete-list-item {
  padding: 12px 15px;
  cursor: pointer;
  border-bottom: 1px solid #3e515b;
  color: #c8ced3;
  transition: all 0.1s ease;
  background-color: #2f353a;
  border-left: 3px solid transparent; /* Prepare for active border */
}

/deep/ .v-autocomplete-list-item:hover,
/deep/ .v-autocomplete-list-item.v-autocomplete-item-active {
  background-color: #343b41; /* Very subtle light-up */
  color: #fff;
  border-left: 3px solid #20a8d8; /* Blue accent instead of full blue background */
  padding-left: 12px; /* Adjust for border */
}

/deep/ .v-autocomplete-list-item:hover .text-muted,
/deep/ .v-autocomplete-list-item.v-autocomplete-item-active .text-muted {
  color: #fff !important; /* Force email readability */
  opacity: 0.7;
}

/deep/ .v-autocomplete-list-item:last-child {
  border-bottom: none;
}
</style>
